/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.initialize();
    }

    initialize() {
      // Move all the constructor logic here
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');

      // Store the original placeholder content for reuse
      const placeholderContent = this.querySelector('.predictive-search');
      this.originalPlaceholderContent = placeholderContent ? placeholderContent.outerHTML : '';

      const header = document.querySelector('.header');
      this.truckSelected = header ? header.getAttribute('data-truck-selected') === 'true' : false;
      this.truckHandleRaw = header ? header.getAttribute('data-truck-filter') : null;
      this.truckTitle = header ? header.getAttribute('data-truck-title') : null;

      // Add flag to track if search is already open
      this.isSearchOpen = false;

      // Add flag to track if search results are currently loading
      this._loadingResults = false;

      // Track the last searched term to avoid unnecessary re-searches
      this._lastSearchedTerm = '';

      // Create debounced getResults function once with custom delay
      this.debouncedGetResults = debounce(this.getResults.bind(this), 200);

      this.searchOffset();
      // Use the existing global debounced resize event system
      this.resizeHandler = this.resizeHandler || this.searchOffset.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);

      this.addListeners();
    }

    // Add re-initialization method
    reinitialize() {
      // Clean up existing listeners
      this.disconnectedCallback();
      // Re-initialize
      this.initialize();
    }

    /**
     * Calculates and sets the search element's position from the left of the screen as a CSS variable
     */
    searchOffset() {
      const rect = this.getBoundingClientRect();
      const leftOffset = rect.left + window.scrollX;
      this.style.setProperty('--search-offset', `${leftOffset}px`);
    }

    // Getter and setter for loadingResults that manages CSS class
    get loadingResults() {
      return this._loadingResults;
    }

    set loadingResults(value) {
      this._loadingResults = value;
      if (value) {
        this.classList.add('loading-results');
      } else {
        // Add a small delay to prevent rapid flickering
        clearTimeout(this._loadingTimeout);
        this._loadingTimeout = setTimeout(() => {
          this.classList.remove('loading-results');
        }, 250);
      }
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      // Clean up event listeners to prevent memory leaks
      if (this.resultsListClickHandler && this.results) {
        this.results.removeEventListener('click', this.resultsListClickHandler);
      }

      // Remove base listeners if present (prevents duplication on reinitialize)
      if (this.input && this._onFocus) this.input.removeEventListener('focus', this._onFocus);
      if (this.input && this._onInput) this.input.removeEventListener('input', this._onInput);
      if (this.productTypeSelect && this._onProductTypeChange) this.productTypeSelect.removeEventListener('change', this._onProductTypeChange);
      const formEl = this.querySelector('.search');
      if (formEl && this._onSubmit) formEl.removeEventListener('submit', this._onSubmit);

      // Clear any pending timeouts
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
      }

      this.close();

      // Remove debounced resize listener
      if (this.resizeHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeHandler);
      }
    }

    addListeners() {
      // Store bound handlers so they can be removed on reinitialize
      this._onFocus = this._onFocus || this.handleFocus.bind(this);
      this._onInput = this._onInput || ((e) => {
        // Only set loading if not already loading
        if (!this.loadingResults) {
          this.loadingResults = true;
        }
        this.handleInput(e);
      });
      this._onProductTypeChange = this._onProductTypeChange || this.handleProductTypeChange.bind(this);
      this._onSubmit = this._onSubmit || this.handleSubmit.bind(this);

      this.input.addEventListener('focus', this._onFocus);
      this.input.addEventListener('input', this._onInput);

      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this._onProductTypeChange);
      }

      this.querySelector('.search').addEventListener('submit', this._onSubmit);
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();

      // If results are loading, defer the submit until results for the current query render
      if (this.loadingResults) {
        evt.preventDefault();
        this.recordSearchTerm(query);
        this._pendingSubmitQuery = query;
        // If debounce hasn't fired yet for this query, force an immediate fetch
        if (query && query !== this._lastSearchedTerm) {
          this.getResults(query);
        }
        return;
      }

      // Compute from current DOM to avoid stale internal flags
      const resultsRoot = this.results || this;
      const exactMatchLink = resultsRoot.querySelector('.js-search-link.has_exact_match');
      const hasTruckFitment = !!resultsRoot.querySelector('li.predictive-search__item.fits_current_truck');

      // If an exact SKU match exists in the results, redirect to its product page.
        if (exactMatchLink) {
          this.recordSearchTerm(query);
          evt.preventDefault();
          window.location.href = exactMatchLink.href;
          return;
      }

      // If this is a truck fitment search, redirect to appropriate search results
      if (this.truckSelected && this.truckHandleRaw) {
        this.recordSearchTerm(query);
        evt.preventDefault();
        if (hasTruckFitment) {
          // Use truck-filtered search if we have truck fitment results
          window.location.href = `/search?q=${encodeURIComponent(query)}&filter.p.m.truck.fitment=${encodeURIComponent(this.truckHandleRaw)}`;
        } else {
          // Use regular search if no truck fitment results
          window.location.href = `/search?q=${encodeURIComponent(query)}`;
        }
        return;
      }

      // Prevent default only if:
      // 1. No query entered, OR
      // 2. A specific search result is currently selected (not the submit button)
      if (!query.length || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) {
        this.recordSearchTerm(query);
        evt.preventDefault();
      } else {
        // Allow normal form submission but still record the search term
        this.recordSearchTerm(query);
      }
    }

    // Centralized navigation logic to be called once results for the current query are rendered
    decideAndNavigate(query) {
      // Compute from current DOM
      const resultsRoot = this.results || this;
      const exactMatchLink = resultsRoot.querySelector('.js-search-link.has_exact_match');
      const hasTruckFitment = !!resultsRoot.querySelector('li.predictive-search__item.fits_current_truck');

      if (exactMatchLink) {
        window.location.href = exactMatchLink.href;
        return;
      }

      if (this.truckSelected && this.truckHandleRaw) {
        if (hasTruckFitment) {
          window.location.href = `/search?q=${encodeURIComponent(query)}&filter.p.m.truck.fitment=${encodeURIComponent(this.truckHandleRaw)}`;
        } else {
          window.location.href = `/search?q=${encodeURIComponent(query)}`;
        }
        return;
      }

      // Fallback: regular search
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.resetResults();
        return;
      }

      this.debouncedGetResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();

      // Only open if not already open
      if (!this.isSearchOpen) {
        this.open();
        if (searchTerm.length) {
          // Only get results if the search term has changed from the last search
          if (searchTerm !== this._lastSearchedTerm) {
            this.getResults(searchTerm);
          } else {
            // If same term, just show the existing results without re-fetching
            this.setAttribute('results', true);
          }
        } else {
          // If no input, show the default search interface with search history
          this.showDefaultInterface();
        }
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          // Prevent double submit when a result is selected
          if (this.querySelector('[aria-selected="true"] > .js-search-link')) {
            evt.preventDefault();
          this.selectOption();
            return;
          }
          break;

        case 'Escape':
          this.close();
          break;

        case 'Backspace':
          if (this.getQuery().length < 2) {
            this.resetResults();
            break;
          }

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      // But don't reset if we're currently loading results
      if (!this.getQuery().length && !this.loadingResults) {
        this.resetResults();
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      // Track the last searched term
      this._lastSearchedTerm = searchTerm;

      this.loadingResults = true;
      this.setLiveRegionLoadingState();

      // Define search fields based on theme settings
      // These fields determine what data is returned in the search results
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      // Build search query, handling product type filter if present
      // If a product type is selected, we'll search within that type only
      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      // Add search parameters for limits and fields
      // These control how many results we get and what data is included
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      // Check cache before making new request
      // This helps reduce server load and improve response time
      const queryKey = searchParams.replace(/\s+/g, '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey], searchTerm);
        // Use the setter for consistent loading state management
        this.loadingResults = false;
        return;
      }

      try {
        // Fetch from three sources: predictive search, normal search, and truck-filtered normal search
        const fetchPromises = [
          fetch(`${theme.routes.predictiveSearch}?${searchParams}`),
          fetch(`/search?q=${encodeURIComponent(searchTerm)}&section_id=embark-normal-search`)
        ];

        // Add truck-filtered search if truck is selected
        if (this.truckSelected && this.truckHandleRaw) {
          fetchPromises.push(
            fetch(`/search?q=${encodeURIComponent(searchTerm)}&section_id=embark-normal-search&filter.p.m.truck.fitment=${encodeURIComponent(this.truckHandleRaw)}`)
          );
        }

        const responses = await Promise.all(fetchPromises);

        // Check all responses
        responses.forEach((response, index) => {
          if (!response.ok) {
            throw new Error(`Response ${index + 1} failed: ${response.status}`);
          }
        });

        // Parse responses into HTML
        const htmlPromises = responses.map(response => response.text());
        const htmlResults = await Promise.all(htmlPromises);

        const predictiveTmpl = document.createElement('template');
        predictiveTmpl.innerHTML = htmlResults[0];
        const normalTmpl = document.createElement('template');
        normalTmpl.innerHTML = htmlResults[1];

        let truckFilteredTmpl;
        if (this.truckSelected && this.truckHandleRaw && htmlResults.length > 2) {
          truckFilteredTmpl = document.createElement('template');
          truckFilteredTmpl.innerHTML = htmlResults[2];
        }

        // Get references to key elements in the predictive search results
        const resultsEl = predictiveTmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) {
          console.error('Predictive search results element not found');
          this.loadingResults = false;
          return;
        }

        const resultsContainer = resultsEl.querySelector('.predictive-search');
        if (!resultsContainer) {
          console.error('Predictive search container not found');
          this.loadingResults = false;
          return;
        }

        const productList = resultsEl.querySelector('#psearch-products ul.predictive-search__list');
        if (!productList) {
          console.error('Product list element not found');
          this.loadingResults = false;
          return;
        }

        const submitButton = productList.querySelector('li.predictive-search__item.js-submit');
        if (!submitButton) {
          console.error('Submit button not found in product list');
          this.loadingResults = false;
          return;
        }

        // Get all product items from all sources
        const predictiveProductItems = Array.from(productList.querySelectorAll('li.predictive-search__item:not(.js-submit)'));
        const normalProductItems = Array.from(normalTmpl.content.querySelectorAll('li.predictive-search__item'));

        let truckFilteredProductItems = [];
        if (truckFilteredTmpl) {
          truckFilteredProductItems = Array.from(truckFilteredTmpl.content.querySelectorAll('li.predictive-search__item'));
        }

        // Combine and prioritize results from all three sources
        const combinedResults = this.combineAndPrioritizeResults(
          predictiveProductItems,
          normalProductItems,
          truckFilteredProductItems,
          searchTerm
        );

        // Clear existing product list (except submit button)
        const existingItems = productList.querySelectorAll('li.predictive-search__item:not(.js-submit)');
        existingItems.forEach(item => item.remove());

        // Add prioritized results
        combinedResults.forEach(item => {
          productList.insertBefore(item, submitButton);
        });

        // Compute match flags locally for UI adjustments
        const hasExactMatch = combinedResults.some(item =>
          item.classList.contains('has_exact_match')
        );
        const hasTruckFitment = combinedResults.some(item =>
          item.classList.contains('fits_current_truck')
        );

        // Add no truck fitment message if needed (as first element)
        if (this.truckSelected && !hasTruckFitment && combinedResults.length > 0) {
          const noResultsMessage = document.createElement('li');
          noResultsMessage.className = 'predictive-search__item';
          noResultsMessage.innerHTML = `
            <div class="predictive-result pt-2 pb-0">
              <p class="mb-0">No results found matching your <b>${this.truckTitle}</b></p>
              <p class="mb-0">Showing all product results.</p>
            </div>
          `;
          productList.insertBefore(noResultsMessage, productList.firstChild);
        }

        // Update submit button text if we have an exact match
        if (hasExactMatch) {
          const submitButtonText = productList.querySelector('.js-submit .predictive-search__view-all span');
          if (submitButtonText) {
            submitButtonText.textContent = 'View Matching Part Number';
          }
        }

        // Handle no results case
        if (combinedResults.length === 0 && !hasTruckFitment) {
          const productTab = resultsEl.querySelector('#psearch-products-tab');
          const productTabbedContent = resultsEl.querySelector('#psearch-products');
          if (productTabbedContent && productTab) {
            // Hide products tab and its content
            productTab.setAttribute('hidden', '');
            productTab.setAttribute('aria-selected', 'false');

            // Select the next available tab
            const nextTab = productTab.nextElementSibling;
            if (nextTab) {
              nextTab.setAttribute('aria-selected', 'true');
            }

            // Hide products content and show next tab's content
            productTabbedContent.setAttribute('hidden', '');
            const nextTabbedContent = productTabbedContent.nextElementSibling;
            if (nextTabbedContent) {
              nextTabbedContent.removeAttribute('hidden');
            }
          }
        }

        // Check if we have any results of any type
        const hasAnyResults =
          combinedResults.length > 0 ||
          resultsContainer.dataset.resourceCollectionsSize !== '0' ||
          resultsContainer.dataset.resourcePagesSize !== '0' ||
          resultsContainer.dataset.resourceArticlesSize !== '0';

        if (!hasAnyResults) {
          // Hide all tabbed content when no results
          const tabbedContent = resultsEl.querySelector('tabbed-content');
          if (tabbedContent) {
            tabbedContent.setAttribute('hidden', '');
          }
          // Show no results message
          const noResults = resultsEl.querySelector('.predictive-search__no-results');
          if (noResults) {
            noResults.removeAttribute('hidden');
          }
        }

        // Replace psearch with actual input ID in the markup
        // This ensures the results are properly associated with the correct search input
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup, searchTerm);
        this.loadingResults = false;
      } catch (error) {
        this.loadingResults = false;
        this.close();
        throw error;
      }
    }

    /**
    * Combines and prioritizes search results from all three sources
    * @param {Array} predictiveItems - Items from predictive search
    * @param {Array} normalItems - Items from normal search
    * @param {Array} truckFilteredItems - Items from truck-filtered normal search
    * @param {string} searchTerm - The search term
    * @returns {Array} Prioritized list of product items
    */
    combineAndPrioritizeResults(predictiveItems, normalItems, truckFilteredItems, searchTerm) {
      const allItems = [...predictiveItems, ...normalItems, ...truckFilteredItems];
      const limit = theme.settings.pSearchLimit;

      // Track handles to avoid duplicates
      const seenHandles = new Set();
      const exactMatches = [];
      const truckFitmentItems = [];
      const remainingItems = [];

      // Process each item
      allItems.forEach(item => {
        const handle = item.getAttribute('data-handle');
        if (!handle || seenHandles.has(handle)) return;
        seenHandles.add(handle);

        // Check for exact SKU match
        const skuElement = item.querySelector('.product-sku__value');
        const isExactMatch = skuElement &&
          skuElement.textContent.toLowerCase() === searchTerm.toLowerCase();

        // Check for truck fitment
        const fitsCurrentTruck = item.classList.contains('fits_current_truck');

        if (isExactMatch) {
          // Mark as exact match
          const link = item.querySelector('.js-search-link');
          if (link) {
            link.classList.add('has_exact_match');
          }
          exactMatches.push(item);
        } else if (fitsCurrentTruck) {
          truckFitmentItems.push(item);
        } else {
          remainingItems.push(item);
        }
      });

      // Combine results in priority order
      const prioritizedResults = [
        ...exactMatches,
        ...truckFitmentItems,
        ...remainingItems
      ];

      // Return only up to the limit
      return prioritizedResults.slice(0, limit);
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup, searchTerm) {
      if (!this.results) {
        console.error('Results container not found');
        return;
      }

      if (!resultsMarkup || typeof resultsMarkup !== 'string') {
        console.error('Invalid results markup provided');
        return;
      }

      // Ignore outdated renders if user has already typed a different query
      if (searchTerm && searchTerm !== this.getQuery()) {
        return;
      }

      try {
        this.results.innerHTML = resultsMarkup;

        this.updateSearchHistory(searchTerm);
        this.renderSearchHistory();

        this.setAttribute('results', true);

        // If a submit was pending for this exact query, navigate now using the rendered DOM
        if (this._pendingSubmitQuery && this._pendingSubmitQuery === this.getQuery()) {
          const pendingQuery = this._pendingSubmitQuery;
          this._pendingSubmitQuery = null;
          this.decideAndNavigate(pendingQuery);
        }
      } catch (error) {
        console.error('Error rendering results:', error);
        this.loadingResults = false;
      }
    }

    recordSearchTerm(searchTerm) {
      // Don't record blank or whitespace-only searches
      if (!searchTerm || !searchTerm.trim()) {
        return;
      }

      try {
        // Retrieve the current search history from localStorage
        let searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];

        // Normalize the search term (e.g., lowercase)
        const normalizedSearchTerm = searchTerm.toLowerCase();

        // Remove the term if it already exists in the history
        searchHistory = searchHistory.filter(term => term.toLowerCase() !== normalizedSearchTerm);

        // Add the new search term to the front of the array
        searchHistory.unshift(searchTerm);

        // Ensure the history only contains the last 3 search terms
        if (searchHistory.length > 3) {
          searchHistory.pop();
        }

        // Update the localStorage with the new history
        localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
      } catch (error) {
        console.error('Error recording search term:', error);
      }
    }

    updateSearchHistory(searchTerm) {
      const resultsList = this.results;

      if (!resultsList) {
        console.error('Results list element not found');
        return;
      }

      if (!searchTerm || typeof searchTerm !== 'string') {
        console.warn('Invalid search term provided to updateSearchHistory:', searchTerm);
        return;
      }

      // Remove any existing event listener to prevent multiple attachments
      if (this.resultsListClickHandler) {
        resultsList.removeEventListener('click', this.resultsListClickHandler);
      }

      // Define the click handler
      this.resultsListClickHandler = (event) => {
        const link = event.target.closest('a');
        if (link) {
          // Optionally, remove the preventDefault in production
          // event.preventDefault();
          // Update search history
          this.recordSearchTerm(searchTerm);

          // Allow default action (navigation)
        }
      };

      // Attach the event listener
      resultsList.addEventListener('click', this.resultsListClickHandler);
    }

    renderSearchHistory() {
      const searchHistoryContainer = this.querySelector('.search-history');

      if (!searchHistoryContainer) {
        console.error('No search history container found.');
        return;
      }

      // Retrieve the search history from localStorage
      const searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];
      if (searchHistory.length === 0) {
        return;
      }

      // Clear only previous search history items, without affecting other content
      const existingItems = searchHistoryContainer.querySelectorAll('.search-history-item');
      if (existingItems.length > 0) {
        existingItems.forEach(item => item.remove());
      }

      // Append new search history items
      searchHistory.forEach(term => {
        if (!term || typeof term !== 'string') {
          console.warn('Invalid search term in history:', term);
          return;
        }

        // Build search URL - append truck filter if truck is selected
        let searchHistoryUrl = `/search?q=${encodeURIComponent(term)}`;
        if (this.truckSelected && this.truckHandleRaw) {
          searchHistoryUrl += `&filter.p.m.truck.fitment=${encodeURIComponent(this.truckHandleRaw)}`;
        }

        const listItem = document.createElement('li');
        listItem.className = 'predictive-search__item';

        listItem.innerHTML = `
          <a class="predictive-result predictive-result--suggestion block focus-inset" href="${searchHistoryUrl}">
            <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M480-120q-138 0-240.5-91.5T122-440h82q14 104 92.5 172T480-200q117 0 198.5-81.5T760-480q0-117-81.5-198.5T480-760q-69 0-129 32t-101 88h110v80H120v-240h80v94q51-64 124.5-99T480-840q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-480q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-120Zm112-192L440-464v-216h80v184l128 128-56 56Z"/>
            </svg>
            <span>${term}</span>
          </a>
          <div class="tap-area close">
            <svg focusable="false" width="10" height="10" class="icon icon--close" viewBox="0 0 14 14">
              <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
            </svg>
          </div>
        `;

        // Add event listener for the close button
        const closeButton = listItem.querySelector('.close');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            this.removeSearchTerm(term);
            listItem.remove();
          });
        }

        // Append the list item to the search history container
        searchHistoryContainer.appendChild(listItem);
      });
    }

    // Helper function to remove a term from search history in localStorage
    removeSearchTerm(term) {
      if (!term || typeof term !== 'string') {
        console.warn('Invalid search term provided to removeSearchTerm:', term);
        return;
      }

      try {
        let searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];

        // Remove the selected term from the history array
        searchHistory = searchHistory.filter(item => item !== term);

        // Update localStorage with the new search history array
        localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
      } catch (error) {
        console.error('Error removing search term:', error);
      }
    }

    /**
     * Shows the default search interface when focusing with no input.
     * This displays search history and a placeholder interface.
     */
    showDefaultInterface() {
      this.results.innerHTML = this.originalPlaceholderContent;
      const placeholder = this.querySelector('.predictive-search-placeholder');
      if (placeholder) {
        placeholder.removeAttribute('hidden');
      }
      this.renderSearchHistory();
    }

    /**
     * Resets the results.
     */
    resetResults() {
      this.input.value = '';
      this.input.focus();

      // Clear the last searched term when resetting
      this._lastSearchedTerm = '';

      // Immediately remove loading state when resetting
      clearTimeout(this._loadingTimeout);
      this._loadingResults = false;
      this.classList.remove('loading-results');

      // Clear any pending deferred submit
      this._pendingSubmitQuery = null;

      this.showDefaultInterface();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.disabled = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      this.isSearchOpen = true;
      document.documentElement.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.resetResults();
      } else {
        // Deselect the selected result (if there is one).
        const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
        if (selected) selected.setAttribute('aria-selected', 'false');

        this.input.blur();
        this.removeAttribute('open');
        this.isSearchOpen = false;
        // this.resetBtn.disabled = true;
        this.overlay.classList.remove('is-visible');
        this.input.setAttribute('aria-activedescendant', '');
        this.input.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('overlay-predictive-search');

        // Remove event listeners added on search results opening.
        this.removeEventListener('keydown', this.keydownHandler);
        if (this.resetBtn && this.resetBtnClickHandler) {
          this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
        }
        if (this.keyupHandler) {
          this.removeEventListener('keyup', this.keyupHandler);
        }
        this.overlay.removeEventListener('click', this.overlayClickHandler);
      }

      // Ensure any pending deferred submit is cleared when closing
      this._pendingSubmitQuery = null;
    }
  }

  // Listen for custom event to re-initialize all instances
  document.addEventListener('dispatch:predictive-search:reinitialize', () => {
    const predictiveSearchElements = document.querySelectorAll('predictive-search');
    predictiveSearchElements.forEach(element => {
      if (element.reinitialize) {
        element.reinitialize();
      }
    });
  });

  customElements.define('predictive-search', PredictiveSearch);
}